/**
 * goPassRankStore — the trainer's GO Pass progress, per event, on-device.
 *
 * 2026-08-27 redesign: progress is a CHECKLIST of milestone ids plus a Deluxe
 * commitment flag (see GoPassMilestone in eventBonuses.ts), replacing the
 * numeric rank counter — trainers think "I hit Rank 25", not "I am rank 27".
 * Ticked milestones drive which pass perks count as active in the floating
 * Bonus bubble and the Bonuses page "Right now" summary. Local-only state
 * (localStorage): pass progress is cosmetic personalisation, not dex data, so
 * it does not sync.
 *
 * Keyed by the feed eventID of the event that carries the pass.
 */
import { useSyncExternalStore } from "react";
import { curatedGoPassFor, type GoPassProgress } from "@/lib/eventBonuses";

export type { GoPassProgress } from "@/lib/eventBonuses";

const STORAGE_KEY = "nexus.goPassChecklist.v1";
/** The pre-checklist rank-counter store; read once to migrate, then ignored. */
const LEGACY_RANK_KEY = "nexus.goPassRank.v1";
const CHANGE_EVENT = "nexus:go-pass-rank-changed";

type Store = Record<string, GoPassProgress>;

function sanitize(v: unknown): GoPassProgress | null {
  if (!v || typeof v !== "object") return null;
  const checked = (v as GoPassProgress).checked;
  if (!Array.isArray(checked)) return null;
  return {
    checked: checked.filter((id): id is string => typeof id === "string"),
    deluxe: Boolean((v as GoPassProgress).deluxe),
  };
}

/**
 * One-time migration from the rank-counter era: a saved rank N ticks every
 * milestone labelled "Rank M" with M <= N (Deluxe-only ones need the flag).
 * Runs only when the checklist key has never been written.
 */
function migrateLegacyRanks(): Store {
  try {
    const raw = localStorage.getItem(LEGACY_RANK_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, { rank?: number; deluxe?: boolean }>;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Store = {};
    for (const [eventId, v] of Object.entries(parsed)) {
      const rank = typeof v?.rank === "number" ? v.rank : 0;
      const deluxe = Boolean(v?.deluxe);
      // Curated passes only: numeric-rank data predates feed-delivered passes.
      const pass = curatedGoPassFor(eventId);
      if (!pass) continue;
      const checked = pass.milestones
        .filter((m) => {
          if (m.deluxeOnly && !deluxe) return false;
          const n = Number(/\d+/.exec(m.label)?.[0]);
          return Number.isFinite(n) && n <= rank;
        })
        .map((m) => m.id);
      if (checked.length > 0 || deluxe) out[eventId] = { checked, deluxe };
    }
    return out;
  } catch {
    return {};
  }
}

function read(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return migrateLegacyRanks();
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Store = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      const p = sanitize(v);
      if (p) out[k] = p;
    }
    return out;
  } catch {
    return {};
  }
}

// Cached snapshot so useSyncExternalStore gets a stable reference between
// changes (a fresh object every getSnapshot call would loop the renderer).
let snapshot: Store = typeof localStorage === "undefined" ? {} : read();

const EMPTY: GoPassProgress = { checked: [], deluxe: false };

export function getGoPassProgress(eventId: string): GoPassProgress {
  return snapshot[eventId] ?? EMPTY;
}

export function setGoPassProgress(eventId: string, progress: GoPassProgress): void {
  const next: Store = {
    ...snapshot,
    [eventId]: {
      checked: [...new Set(progress.checked)],
      deluxe: progress.deluxe,
    },
  };
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full/blocked: the in-memory snapshot still serves this session.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Tick or untick one milestone. Reads the store fresh, so rapid taps stack. */
export function toggleGoPassMilestone(eventId: string, milestoneId: string): void {
  const cur = getGoPassProgress(eventId);
  const has = cur.checked.includes(milestoneId);
  setGoPassProgress(eventId, {
    ...cur,
    checked: has ? cur.checked.filter((id) => id !== milestoneId) : [...cur.checked, milestoneId],
  });
}

function subscribe(onChange: () => void): () => void {
  const handler = () => {
    snapshot = read();
    onChange();
  };
  window.addEventListener(CHANGE_EVENT, handler);
  // Cross-tab writes arrive as storage events.
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

function getSnapshot(): Store {
  return snapshot;
}

/** Reactive view of the whole store; index with getGoPassProgress semantics. */
export function useGoPassRanks(): Store {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
